import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Locale } from '@/i18n';
import type { Language } from '@/hooks/useTranslations';

export interface SystemSetting {
  id: string;
  key: string;
  value_json: unknown;
  description: string | null;
  updated_at: string;
  updated_by: string | null;
}

const SETTINGS_KEY = ['system-settings'];

export const useSystemSettings = () => {
  return useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: async () => {
      // Authenticated users (admins) get full table via RLS; anon users
      // get the public-safe view exposing only allowlisted keys. We try
      // the table first and fall back to the sanitized view on permission
      // denied so anon callers still receive public config (site_name,
      // contact_email, maintenance_mode, ...).
      const { data: { session } } = await supabase.auth.getSession();
      const source = session ? 'system_settings' : 'public_system_settings';
      const { data, error } = await (supabase as any)
        .from(source)
        .select('*');
      if (error) throw error;
      return (data ?? []) as SystemSetting[];
    },
    staleTime: 60_000,
  });
};

export const useSettingValue = <T = unknown>(key: string, fallback: T): T => {
  const { data } = useSystemSettings();
  if (!data) return fallback;
  const row = data.find((s) => s.key === key);
  return row ? (row.value_json as T) : fallback;
};

/**
 * Returns active language codes from the `languages` table (single source of truth).
 * Falls back to all four locales while loading.
 */
export const useActiveLanguages = (): Locale[] => {
  const { data: languages = [] } = useQuery({
    queryKey: ['languages'],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from('languages').select('*').order('sort_order');
      if (error) throw error;
      return (data || []) as Language[];
    },
    staleTime: 1000 * 60 * 10,
  });
  const active = languages.filter(l => l.is_active).map(l => l.code as Locale);
  return active.length > 0 ? active : ['de', 'en', 'fr', 'it'];
};

export const useUpdateSetting = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      const { error } = await supabase.functions.invoke('system-setting-write', {
        body: { command: 'update_setting', key, value },
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
};

/**
 * Liest die letzten Wartungsmodus-Toggles (aktiviert/deaktiviert) aus
 * `audit_logs`. Admin-only via RLS. Liefert User-Display-Name + Zeitpunkt.
 */
export interface MaintenanceAuditEntry {
  id: string;
  actor_id: string | null;
  action: 'maintenance_mode_enabled' | 'maintenance_mode_disabled';
  created_at: string;
  comment: string | null;
  actor_name: string | null;
  actor_email: string | null;
}

export const useMaintenanceAuditLog = (limit = 20) => {
  return useQuery({
    queryKey: ['maintenance-audit-log', limit],
    queryFn: async (): Promise<MaintenanceAuditEntry[]> => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('id, actor_id, action, created_at, comment')
        .in('action', ['maintenance_mode_enabled', 'maintenance_mode_disabled'])
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;

      const rows = (data ?? []) as Array<{
        id: string;
        actor_id: string | null;
        action: string;
        created_at: string;
        comment: string | null;
      }>;

      const actorIds = Array.from(
        new Set(rows.map((r) => r.actor_id).filter((v): v is string => !!v)),
      );
      const profileMap = new Map<string, { name: string | null; email: string | null }>();
      if (actorIds.length) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name, email')
          .in('id', actorIds);
        for (const p of (profiles ?? []) as Array<{
          id: string;
          display_name: string | null;
          email: string | null;
        }>) {
          profileMap.set(p.id, { name: p.display_name, email: p.email });
        }
      }

      return rows.map((r) => ({
        id: r.id,
        actor_id: r.actor_id,
        action: r.action as MaintenanceAuditEntry['action'],
        created_at: r.created_at,
        comment: r.comment,
        actor_name: r.actor_id ? profileMap.get(r.actor_id)?.name ?? null : null,
        actor_email: r.actor_id ? profileMap.get(r.actor_id)?.email ?? null : null,
      }));
    },
    staleTime: 30_000,
  });
};
